import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../contexts/LanguageContext";
import { List, X } from "lucide-react";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/edcc2150-0b82-47f9-839c-552e0da40dc2/images/70bd0f022afb547c51af551a9eca883fb3912ac76a7a6f74fc7b6ed18d9448fe.png";

export default function Navbar() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: t("nav.services"), href: "#services" },
    { label: t("nav.cases"), href: "#cases" },
    { label: t("nav.pricing"), href: "#pricing" },
    { label: t("nav.process"), href: "#process" },
    { label: t("nav.contact"), href: "#contact" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-16 md:h-20">
        <a href="#" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-3" data-testid="nav-logo">
          <img src={LOGO_URL} alt="wedowedo" className="h-8 w-8" />
          <span className="font-outfit font-medium text-white text-lg tracking-tight">wedowedo</span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              data-testid={`nav-link-${link.href.slice(1)}`}
              className="text-sm text-neutral-400 hover:text-white transition-colors duration-200 font-manrope"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center border border-white/10 rounded-sm overflow-hidden font-mono text-xs tracking-[0.2em]">
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-white/10 text-white" : "text-neutral-500 hover:text-white"}`}
            >
              EN
            </button>
            <button
              data-testid="lang-switch-sv"
              onClick={() => setLang("sv")}
              className={`px-3 py-1.5 transition-colors ${lang === "sv" ? "bg-white/10 text-white" : "text-neutral-500 hover:text-white"}`}
            >
              SV
            </button>
          </div>
          <a
            data-testid="nav-cta-button"
            href="/onboarding"
            className="bg-transparent border border-[#00F0FF] text-[#00F0FF] shadow-[0_0_15px_rgba(0,240,255,0.3)] hover:bg-[#00F0FF] hover:text-black hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-all duration-300 rounded-sm px-5 py-2 text-sm font-medium inline-block"
          >
            {t("nav.cta")}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden text-white p-2"
        >
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-[#050505]/95 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="text-left text-neutral-300 hover:text-white transition-colors font-manrope"
                >
                  {link.label}
                </button>
              ))}
              <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                <button onClick={() => setLang("en")} className={`font-mono text-xs tracking-[0.2em] px-3 py-1.5 ${lang === "en" ? "text-white" : "text-neutral-500"}`}>EN</button>
                <button onClick={() => setLang("sv")} className={`font-mono text-xs tracking-[0.2em] px-3 py-1.5 ${lang === "sv" ? "text-white" : "text-neutral-500"}`}>SV</button>
              </div>
              <button
                onClick={() => scrollTo("#contact")}
                className="border border-[#00F0FF] text-[#00F0FF] rounded-sm px-5 py-2.5 text-sm font-medium mt-2"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
